import { useCallback, useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import axiosInstance from "config/axiosInstance";
import Table from "examples/Tables/Table";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";
import WaOutlineButton from "./WaOutlineButton";
import { formatTimePeru } from "utils/datePeru";
import { useAppAlert } from "hooks/useAppAlert";
import RecipientNameCell from "./RecipientNameCell";
import {
  formatMonto,
  formatOrigenLabel,
  getStatusUi,
} from "utils/whatsappUi";

export default function DeliveryHistoryPanel({ refreshKey = 0 }) {
  const { showAlert, AlertBanner } = useAppAlert();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/whatsapp/queue/history", { params: { limit: 150 } });
      if (res.data.success) {
        setItems(res.data.items || []);
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleRetry = async (queueId) => {
    setActionId(queueId);
    try {
      await axiosInstance.post(`/whatsapp/queue/${queueId}/retry`);
      showAlert("Mensaje programado para reenvío", "success");
      load();
    } catch (err) {
      showAlert(err.response?.data?.message || "No se pudo reenviar", "error");
    } finally {
      setActionId(null);
    }
  };

  const handleCancel = async (queueId) => {
    setActionId(queueId);
    try {
      await axiosInstance.post(`/whatsapp/queue/${queueId}/cancel`);
      showAlert("Envío cancelado", "info");
      load();
    } catch (err) {
      showAlert(err.response?.data?.message || "No se pudo cancelar", "error");
    } finally {
      setActionId(null);
    }
  };

  const handleRetryFailed = async () => {
    setActionId("bulk");
    try {
      const res = await axiosInstance.post("/whatsapp/queue/retry-failed");
      const n = res.data.requeued || 0;
      showAlert(
        n > 0 ? `${n} mensaje(s) fallido(s) en cola de reenvío` : "No hay fallidos pendientes",
        n > 0 ? "success" : "info"
      );
      load();
    } catch (err) {
      showAlert(err.response?.data?.message || "Error al reenviar fallidos", "error");
    } finally {
      setActionId(null);
    }
  };

  const rows = items.map((item) => {
    const statusKey = item.displayStatus || item.estado;
    const ui = getStatusUi(statusKey);
    const canRetry = statusKey === "error" || statusKey === "cancelado";
    const canCancel = statusKey === "pendiente" || statusKey === "retry" || statusKey === "enviando";

    return {
      hora: formatTimePeru(item.hora || item.sent_at || item.created_at),
      destinatario: (
        <RecipientNameCell
          nombre={item.destinatario_nombre}
          telefono={item.telefono_destino}
        />
      ),
      estado: (
        <Chip
          label={`${ui.emoji} ${ui.label}`}
          size="small"
          color={ui.color}
          variant="filled"
        />
      ),
      monto: formatMonto(item.monto),
      origen: formatOrigenLabel(item.destino_origen),
      acciones: (canRetry || canCancel) && (
        <VuiBox display="flex" gap={0.5}>
          {canRetry && (
            <WaOutlineButton
              size="small"
              onClick={() => handleRetry(item.id)}
              disabled={actionId === item.id}
            >
              Reenviar
            </WaOutlineButton>
          )}
          {canCancel && (
            <WaOutlineButton
              size="small"
              onClick={() => handleCancel(item.id)}
              disabled={actionId === item.id}
            >
              Cancelar
            </WaOutlineButton>
          )}
        </VuiBox>
      ),
    };
  });

  return (
    <VuiBox>
      <AlertBanner />
      <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
        <VuiBox>
          <VuiTypography variant="h5" color="white" fontWeight="bold" mb={0.5}>
            Historial de envíos
          </VuiTypography>
          <VuiTypography variant="button" color="text">
            Cada destinatario con su propio estado
          </VuiTypography>
        </VuiBox>
        <VuiBox display="flex" gap={1} flexWrap="wrap">
          <WaOutlineButton size="small" onClick={load} disabled={loading}>
            Actualizar
          </WaOutlineButton>
          <VuiButton
            color="warning"
            variant="gradient"
            size="small"
            onClick={handleRetryFailed}
            disabled={actionId === "bulk"}
          >
            Reenviar fallidos
          </VuiButton>
        </VuiBox>
      </VuiBox>

      <Card sx={{ border: "1px solid rgba(255,255,255,0.06)" }}>
        <VuiBox sx={{ overflowX: "auto" }}>
          {loading && items.length === 0 ? (
            <VuiBox p={2}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} height={36} sx={{ mb: 1, bgcolor: "rgba(255,255,255,0.06)" }} />
              ))}
            </VuiBox>
          ) : (
            <>
              <Table
                brightHeader
                columns={[
                  { name: "hora", label: "Hora", align: "left" },
                  { name: "destinatario", label: "Destinatario", align: "left" },
                  { name: "estado", label: "Estado", align: "center" },
                  { name: "monto", label: "Monto", align: "right" },
                  { name: "origen", label: "Origen", align: "center" },
                  { name: "acciones", label: "Acciones", align: "center" },
                ]}
                rows={rows}
              />
              {!loading && rows.length === 0 && (
                <VuiBox py={4} textAlign="center">
                  <VuiTypography variant="button" color="text">
                    Sin envíos registrados
                  </VuiTypography>
                </VuiBox>
              )}
            </>
          )}
        </VuiBox>
      </Card>
    </VuiBox>
  );
}
